import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../context/ThemeContext";

const Navbar: React.FC = () => {
  const [active, setActive] = useState("Home");
  const [isOpen, setIsOpen] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();

  const navLinks = useMemo(() => ["Home", "Services", "TechStacks", "About", "Projects"], []);

  const handleContactClick = () => {
    window.open(
      "https://mail.google.com/mail/?view=cm&fs=1&to=jessilebonzo.dev@gmail.com",
      "_blank"
    );
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleNavLinkClick = (link: string) => {
    setActive(link);
    
    const element = document.getElementById(link.toLowerCase());
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    
    if (isOpen) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-20% 0px -70% 0px",
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          const linkName = sectionId.charAt(0).toUpperCase() + sectionId.slice(1);
          
          if (navLinks.includes(linkName)) {
            setActive(linkName);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    navLinks.forEach((link) => {
      const element = document.getElementById(link.toLowerCase());
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [navLinks]);

  const baseNavClasses = `${isDarkMode ? 'bg-[#0A181F] text-white' : 'bg-gray-200 text-gray-800'} px-6 md:px-16 lg:px-24 py-4 flex justify-between items-center font-montserrat text-lg relative transition-colors duration-300`;
  
  const hamburgerLineClasses = `block h-0.5 w-6 ${isDarkMode ? 'bg-white' : 'bg-gray-800'} rounded-sm transition-all duration-300`;
  
  const mobileMenuClasses = `absolute top-full left-0 right-0 ${isDarkMode ? 'bg-[#0A181F]' : 'bg-white'} shadow-lg py-4 z-50 md:hidden`;

  return (
    <nav className={baseNavClasses}>
      <div className="flex items-center space-x-2">
        <span className="text-2xl font-extrabold">JB</span>
        <span className="text-lg">Engineer</span>
        <button 
          onClick={toggleTheme} 
          className={`ml-2 p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'} transition-colors`}
          aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDarkMode ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
            </svg>
          )}
        </button>
      </div>
      
      <button 
        className="md:hidden flex flex-col justify-center items-center"
        onClick={toggleMenu}
        aria-label="Toggle navigation menu"
      >
        <span className={`${hamburgerLineClasses} ${isOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
        <span className={`${hamburgerLineClasses} my-1 ${isOpen ? 'opacity-0' : ''}`}></span>
        <span className={`${hamburgerLineClasses} ${isOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
      </button>
              <ul className="hidden md:flex items-center space-x-4 lg:space-x-8">
                {navLinks.map((link) => (
                  <li key={link} className="flex items-center">
                    <a
                      href={`#${link.toLowerCase()}`}
                      className={`relative font-bold text-sm lg:text-md hover:text-[#A988D8] transition-all ${
                        active === link ? (isDarkMode ? "text-white" : "text-gray-900") : (isDarkMode ? "text-gray-200" : "text-gray-600")
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavLinkClick(link);
                      }}
                    >
                      {link}
                      {active === link && (
                        <motion.div
                          className="absolute left-0 top-full h-1 bg-purple-500 rounded-full"
                          layoutId="underline"
                          initial={{ width: 0 }}
                          animate={{ width: "100%" }}
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </a>
                  </li>
                ))}
                <li className="flex items-center">
                  <button 
                    onClick={handleContactClick}
                    className="bg-gradient-to-r from-[#83329A] to-[#A988D8] text-white px-4 lg:px-6 py-2 rounded-full font-semibold shadow-md hover:opacity-80 transition-all text-sm lg:text-base"
                  >
                    Contact
                  </button>
                </li>
              </ul>
      {isOpen && (
        <motion.div 
          className={mobileMenuClasses}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <ul className="flex flex-col items-center space-y-4 py-2">
            {navLinks.map((link) => (
              <li key={link} className="w-full text-center">
                <div className="flex items-center justify-center">
                  <a
                    href={`#${link.toLowerCase()}`}
                    className={`block py-2 px-4 relative font-bold hover:text-[#A988D8] transition-all ${
                      active === link ? 
                        (isDarkMode ? "text-[#A988D8]" : "text-[#83329A]") : 
                        (isDarkMode ? "text-gray-200" : "text-gray-700")
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavLinkClick(link);
                    }}
                  >
                    {link}
                  </a>
                </div>
              </li>
            ))}
            <li className="w-full flex justify-center pt-2">
              <button 
                onClick={() => {
                  handleContactClick();
                  setIsOpen(false);
                }}
                className="bg-gradient-to-r from-[#83329A] to-[#A988D8] text-white px-6 py-2 rounded-full font-semibold shadow-md hover:opacity-80 transition-all w-4/5"
              >
                Contact
              </button>
            </li>
          </ul>
        </motion.div>
      )}
    </nav>
  );
};

export default Navbar;